import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Save, Upload, Eye } from "lucide-react";
import Api from "../../../../shared/api/api";
import "../styles/AdminBookstores.css";

const FORMATS = ["paperback", "hardcover"];

function BookPrices({ book, prices, locked }) {
  const [values, setValues] = useState(() => Object.fromEntries(FORMATS.map(format => {
    const row = prices.find(price => price.book_title === book.title && price.format === format);
    return [format, { bookstore_price: row?.bookstore_price ?? "", msrp: row?.msrp ?? "", discount_percent: row?.discount_percent ?? "" }];
  })));
  const [saving, setSaving] = useState(false), [error, setError] = useState(""), [notice, setNotice] = useState("");
  const change = (format, field, value) => {
    setValues(current => {
      const row = { ...current[format], [field]: value };
      if (field === "bookstore_price") row.discount_percent = "";
      if (field !== "bookstore_price" && row.msrp !== "" && row.discount_percent !== "" && Number(row.discount_percent) >= 0 && Number(row.discount_percent) < 100) {
        row.bookstore_price = (Math.round(Math.round(Number(row.msrp) * 100) * (10000 - Math.round(Number(row.discount_percent) * 100)) / 10000) / 100).toFixed(2);
      }
      return { ...current, [format]: row };
    });
    setNotice("");
  };
  const save = async event => {
    event.preventDefault(); setSaving(true); setError(""); setNotice("");
    try {
      const result = await Api.saveBookstorePricing({ book_title: book.title, formats: FORMATS.map(format => ({ format, ...values[format] })) });
      if (result.formats) setValues(Object.fromEntries(result.formats.map(row => [row.format, { bookstore_price: row.bookstore_price ?? "", msrp: row.msrp ?? "", discount_percent: row.discount_percent ?? "" }])));
      setNotice("Pricing saved.");
    } catch (err) { setError(err.message); } finally { setSaving(false); }
  };
  return <form className="bookstore-pricing-book" onSubmit={save} aria-label={`${book.title.trim()} pricing`}>
    <header><h2>{book.title.trim()}</h2></header>
    <fieldset disabled={saving || locked}><div className="bookstore-price-grid">
      {FORMATS.map(format => <div key={format}><h3>{format === "paperback" ? "Paperback" : "Hardcover"}</h3>
        <div className="bookstore-fields">{[['msrp', 'MSRP (USD)'], ['discount_percent', 'Bookstore discount (%)'], ['bookstore_price', 'Bookstore price (USD)']].map(([field, label]) => <label key={field}>{label}<input type="number" aria-label={`${book.title.trim()} ${format} ${label}`} min={field === "discount_percent" ? "0" : "0.01"} max={field === "discount_percent" ? "99.99" : "999999.99"} step="0.01" value={values[format][field]} onChange={event => change(format, field, event.target.value)} /></label>)}</div>
      </div>)}
    </div></fieldset>
    {error && <p role="alert" className="bookstore-error">{error}</p>}
    {notice && <p role="status" className="bookstore-notice">{notice}</p>}
    <div className="bookstore-actions"><button type="submit" className="bookstore-primary" disabled={saving || locked}><Save size={17} />{saving ? "Saving..." : "Save pricing"}</button></div>
  </form>;
}

export default function AdminBookstorePricing() {
  const [data, setData] = useState(null), [error, setError] = useState("");
  const [file, setFile] = useState(null), [preview, setPreview] = useState(null), [busy, setBusy] = useState(false), [notice, setNotice] = useState("");
  const [revision, setRevision] = useState(0);
  const previewImport = async () => {
    if (!file || busy) return;
    setBusy(true); setError(""); setNotice(""); setPreview(null);
    try { setPreview(await Api.importBookstorePricing(file)); }
    catch (err) { setError(err.message); }
    finally { setBusy(false); }
  };
  const applyImport = async () => {
    if (!file || !preview?.changes.length || busy || !window.confirm(`Apply pricing for ${preview.changes.length} editions? Unsaved pricing edits on this page will be replaced.`)) return;
    setBusy(true); setError(""); setNotice("");
    try {
      const result = await Api.importBookstorePricing(file, preview.preview_token);
      setPreview(null);
      setNotice(`Bookstore pricing updated for ${result.updated} ${result.updated === 1 ? "edition" : "editions"}.`);
      const pricing = await Api.getBookstorePricing();
      setData(current => ({ ...current, prices: pricing.prices })); setRevision(value => value + 1);
    } catch (err) {
      setError(err.message);
      setPreview(null);
    } finally { setBusy(false); }
  };
  useEffect(() => {
    let active = true;
    Promise.all([Api.getBookstoreBooks(), Api.getBookstorePricing()]).then(([catalog, pricing]) => { if (active) setData({ books: catalog.books, prices: pricing.prices }); }).catch(err => { if (active) setError(err.message); });
    return () => { active = false; };
  }, []);
  return <div className="bookstore-page">
    <Link className="bookstore-back" to="/admin/bookstores"><ArrowLeft size={17} />Bookstores</Link>
    <header className="bookstore-header"><h1>Bookstore Pricing</h1><span className="bookstore-muted">USD</span></header>
    {error && <p role="alert" className="bookstore-error">{error}</p>}
    {notice && <p role="status" className="bookstore-notice">{notice}</p>}
    {!data && !error && <p role="status">Loading pricing...</p>}
    {data && <section className="bookstore-pricing-import" aria-labelledby="pricing-import-heading">
      <h2 id="pricing-import-heading">IngramSpark Pricing</h2>
      <div className="bookstore-template-row">
        <label>Title pricing CSV<input type="file" accept=".csv,text/csv" disabled={busy} onChange={event => { setFile(event.target.files[0] || null); setPreview(null); setNotice(""); setError(""); }} /></label>
        <button type="button" disabled={!file || busy} onClick={previewImport}><Eye size={17} />{busy ? "Working..." : "Preview import"}</button>
      </div>
      {preview && <>
        <h3>{preview.changes.length} matched print {preview.changes.length === 1 ? "edition" : "editions"}</h3>
        <div className="bookstore-table-wrap"><table className="bookstore-table"><thead><tr><th>Book / ISBN</th><th>Format</th><th>Current MSRP / bookstore price (USD)</th><th>New MSRP (USD)</th><th>Discount</th><th>New bookstore price (USD)</th></tr></thead>
          <tbody>{preview.changes.map(row => <tr key={`${row.book_title}-${row.format}`}><td>{row.book_title}<small>{row.isbn}</small></td><td>{row.format}</td><td>{row.previous ? `$${row.previous.msrp} / $${row.previous.bookstore_price}` : "Not set"}</td><td>${row.msrp}</td><td>{row.discount_percent}%</td><td>${row.bookstore_price}</td></tr>)}</tbody></table></div>
        {preview.skipped.length > 0 && <details className="bookstore-import-skipped"><summary>Skipped editions ({preview.skipped.length})</summary><ul>{preview.skipped.map(row => <li key={row.source_row}>{row.title} ({row.isbn}): {row.reason}</li>)}</ul></details>}
        <div className="bookstore-actions"><button type="button" className="bookstore-primary" disabled={busy || !preview.changes.length} onClick={applyImport}><Upload size={17} />Apply pricing import</button></div>
      </>}
    </section>}
    {data?.books.map(book => <BookPrices key={`${revision}-${book.title}`} book={book} prices={data.prices} locked={busy} />)}
  </div>;
}
